import { Link } from "react-router-dom";
import { BRAND_NAME } from "@/constants/brand";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Stethoscope,
  MessageCircle,
  Activity,
} from "lucide-react";
import MembershipCardPreview from "@/components/MembershipCardPreview";

export const Hero = () => {
  return (
    <section className="relative overflow-hidden pt-28 pb-20 sm:pt-32 sm:pb-28">
      {/* Decorative backgrounds */}
      <div className="absolute inset-0 bg-gradient-hero" />
      <div className="absolute inset-0 grid-pattern opacity-50" />
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] bg-aqua-glow blur-2xl" />
      <div className="absolute top-1/3 -left-32 h-[380px] w-[380px] bg-aqua-glow blur-3xl opacity-70" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <Badge
              variant="secondary"
              className="bg-background/70 backdrop-blur border border-primary/15 text-primary px-3.5 py-1.5 rounded-full mb-6 animate-fade-in"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Annual Membership · Just ₹100/year
            </Badge>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-foreground animate-fade-up">
              Your family’s health,
              <br className="hidden sm:block" />{" "}
              <span className="text-gradient-aqua">on one tiny card.</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: "80ms" }}>
              The <span className="text-foreground font-medium">{BRAND_NAME} Membership</span> unlocks free doctor consultations and
              <span className="text-foreground font-medium"> 10% off every diagnostic</span> — blood tests, CT scan, MRI, X-Ray and sonography — across all our clinics. Generated in 30 seconds, delivered straight to your WhatsApp.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 animate-fade-up" style={{ animationDelay: "160ms" }}>
              <Link to="/generate">
                <Button size="lg" className="h-12 px-6 bg-gradient-primary text-primary-foreground hover:opacity-95 shadow-elegant rounded-xl group">
                  <Sparkles className="h-5 w-5" />
                  Generate my card – ₹100
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <a href="#how">
                <Button variant="outline" size="lg" className="h-12 px-5 rounded-xl border-primary/20 hover:bg-primary/5">
                  See how it works
                </Button>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl animate-fade-up" style={{ animationDelay: "240ms" }}>
              {[
                { icon: Stethoscope, label: "Free consults" },
                { icon: Activity, label: "10% off tests" },
                { icon: MessageCircle, label: "Card on WhatsApp" },
                { icon: ShieldCheck, label: "Secure payment" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex flex-col items-start gap-2 rounded-xl border border-border/60 bg-background/60 backdrop-blur px-3 py-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-xs font-medium text-foreground leading-tight">{label}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-4 animate-fade-up" style={{ animationDelay: "320ms" }}>
              <div className="flex -space-x-3">
                {[
                  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=96&h=96&fit=crop",
                  "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=96&h=96&fit=crop",
                  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=96&h=96&fit=crop",
                  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=96&h=96&fit=crop",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className="h-9 w-9 rounded-full ring-2 ring-background object-cover"
                  />
                ))}
              </div>
              <div className="text-xs sm:text-sm">
                <div className="font-semibold text-foreground">12,400+ active members</div>
                <div className="text-muted-foreground">trust 1Rupee Clinic across 18+ locations</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="animate-float-slow">
              <MembershipCardPreview
                name="Aarav Sharma"
                mobile="9876543210"
                membershipNumber="XXXXXXXX"
                issued="24 Jan 2025"
                expires="24 Jan 2026"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden sm:flex items-center gap-3 rounded-2xl bg-background/85 backdrop-blur-xl border border-border shadow-elegant px-4 py-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-success/15 text-success">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <div className="text-sm font-semibold text-foreground">Payment secured</div>
                <div className="text-xs text-muted-foreground">via Razorpay</div>
              </div>
            </div>
            <div className="absolute -top-4 -right-2 hidden sm:flex items-center gap-3 rounded-2xl bg-background/85 backdrop-blur-xl border border-border shadow-elegant px-4 py-3">
              <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 text-accent">
                <MessageCircle className="h-5 w-5" />
                <span className="absolute inset-0 rounded-full animate-pulse-ring" />
              </span>
              <div>
                <div className="text-sm font-semibold text-foreground">Card sent</div>
                <div className="text-xs text-muted-foreground">on WhatsApp</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
